import axiosInstance from './axios';

export interface Post {
  ID: string;
  Title: string;
  Platform: string;
  Category: string;
  Tags: string[];
  Flag: string;
  Encrypted: boolean;
  Visibility: 'public' | 'private';
  ImageURL: string;
  Path: string;
  CreatedAt?: string;
  UpdatedAt?: string;
}

export interface PostFormData {
  title: string;
  platform: string;
  category: string;
  tags: string[];
  flag: string;
  encrypted: boolean;
  visibility: 'public' | 'private';
  zipfile: File | null;
  ImageURL: 'https://htb-mp-prod-public-storage.s3.eu-central-1.amazonaws.com/avatars/ca7a993af1eef2b5676f39dccd8fbb2f.png';
}

interface PostResponse {
  filedata: string;
}

interface TagResponse {
  tag: string;
  count: number;
}

export interface AdminStats {
  posts: { total: number; public: number; private: number; encrypted: number };
  users: number;
  categories: Array<{ name: string; count: number }>;
  platforms: Array<{ name: string; count: number }>;
  recent: Post[];
}


// GET ALL POSTS
export const fetchPosts = async (): Promise<Post[]> => {
  const response = await axiosInstance.get('/posts/');
  return response.data;
};

// VIEW SINGLE POST
export const viewPost = async (id: string, flag?: string): Promise<PostResponse> => {
  if (flag) {
    const response = await axiosInstance.post(`/posts/view/${id}/unlock`, { flag });
    return response.data;
  }
  const response = await axiosInstance.get(`/posts/view/${id}`, {
    params: undefined,
  });
  return response.data;
};

// GET CATEGORIES
export const fetchCategories = async (): Promise<string[]> => {
  const response = await axiosInstance.get('/posts/categories');
  return response.data.categories;
};

// GET TAGS
export const fetchTags = async (): Promise<TagResponse[]> => {
  const response = await axiosInstance.get('/posts/tags');
  return response.data.tags;
};

// SEARCH POSTS
export const searchPosts = async (params: {
  title?: string;
  platform?: string;
  category?: string;
  tags?: string[];
  page?: number;
  pageSize?: number;
  sort?: 'title' | 'updatedAt' | 'createdAt';
  order?: 'asc' | 'desc';
  visibility?: 'public' | 'private';
  encrypted?: boolean;
}): Promise<{ count: number; posts: Post[]; page: number; pageSize: number; totalPages: number; message: string }> => {
  // Build URLSearchParams manually to handle arrays properly
  const searchParams = new URLSearchParams();
  
  // Add non-empty parameters
  if (params.title?.trim()) {
    searchParams.append('title', params.title.trim());
  }
  
  if (params.platform?.trim()) {
    searchParams.append('platform', params.platform.trim());
  }
  
  if (params.category?.trim()) {
    searchParams.append('category', params.category.trim());
  }
  
  // Handle tags array properly
  if (params.tags && params.tags.length > 0) {
    params.tags.forEach(tag => {
      if (tag.trim()) {
        searchParams.append('tags', tag.trim());
      }
    });
  }

  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params.sort) searchParams.set('sort', params.sort);
  if (params.order) searchParams.set('order', params.order);
  if (params.visibility) searchParams.set('visibility', params.visibility);
  if (typeof params.encrypted === 'boolean') searchParams.set('encrypted', String(params.encrypted));

  
  try {
    // Use the search params as a query string
    const response = await axiosInstance.get(`/posts/search?${searchParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Search API error:', error);
    throw error;
  }
};

// CREATE NEW POST
export const createPost = async (formData: FormData): Promise<Post> => {
  try {
    const response = await axiosInstance.post('/posts/new', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    // My backend returns: {"message": "Post created successfully", "post": post}
    return response.data.post;

  } catch (error: any) {
    // Better error handling
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Failed to create post');
  }
};

// UPDATE POST
export const updatePost = async (id: string, formData: FormData): Promise<void> => {
  try {
    await axiosInstance.post(`/posts/update/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  } catch (error: any) {
    // Better error handling
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Failed to update post');
  }
};

// DELETE POST
export const deletePost = async (id: string): Promise<void> => {
  await axiosInstance.post(`/posts/delete/${id}`);
};

export const fetchAdminStats = async (): Promise<AdminStats> => {
  const response = await axiosInstance.get('/posts/admin/stats');
  return response.data;
};
